/**
 * Draw the walkthrough's PDF, email and text previews for any document.
 * Message content comes from the server's send composers. Email HTML runs
 * in an opaque sandbox; SMS content is text, and its one actionable link
 * leads only to the signed-in owner's client preview. The run owns where
 * these previews appear and cancels a PDF render when it leaves.
 */
import { ensurePdfJs } from './pdf_email_preview_controller.js';

/**
 * Draw the client's email without allowing scripts, forms or top navigation.
 * Same origin only so its logo and photos load with the owner's session
 * (email_template.preview_quote_urls); with no allow-scripts it runs nothing.
 */
export function drawEmail(container, message) {
    const subject = document.createElement('h3');
    subject.className = 'walkthrough-message__subject';
    subject.textContent = message.subject;
    const frame = document.createElement('iframe');
    frame.className = 'walkthrough-message__email';
    frame.setAttribute('sandbox', 'allow-same-origin');
    frame.setAttribute('referrerpolicy', 'no-referrer');
    frame.title = message.subject;
    frame.srcdoc = message.html;
    container.replaceChildren(subject, frame);
}

/** Draw a phone bubble whose displayed message link opens the owner preview. */
export function drawText(container, message, ownerUrl) {
    const phone = document.createElement('p');
    phone.className = 'walkthrough-message__phone';
    phone.textContent = message.phone;
    const bubble = document.createElement('div');
    bubble.className = 'walkthrough-message__bubble';
    const text = document.createElement('span');
    const at = message.body.indexOf(message.link);
    text.textContent = at < 0 ? message.body : message.body.slice(0, at);
    const link = document.createElement('a');
    link.setAttribute('href', ownerUrl);
    link.textContent = message.link;
    const after = document.createElement('span');
    after.textContent = at < 0 ? '' : message.body.slice(at + message.link.length);
    bubble.append(text, link, after);
    container.replaceChildren(phone, bubble);
    return { link };
}

/**
 * Draw each PDF page as a responsive canvas, including in Android WebViews:
 * drawn at the screen's pixel density and shown at the container's width
 * (onboarding.css sizes it), so the small print stays sharp.
 */
export async function drawPdf(container, url, pdfjsSrc, { signal } = {}) {
    await ensurePdfJs(pdfjsSrc);
    signal?.throwIfAborted();
    const loading = window.pdfjsLib.getDocument({ url, disableRange: true, disableStream: true });
    const cancel = () => { loading.destroy().catch(() => {}); };
    signal?.addEventListener('abort', cancel, { once: true });
    try {
        const pdf = await loading.promise;
        container.replaceChildren();
        for (let index = 1; index <= pdf.numPages; index++) {
            signal?.throwIfAborted();
            const page = await pdf.getPage(index);
            const canvas = document.createElement('canvas');
            const natural = page.getViewport({ scale: 1 });
            const density = window.devicePixelRatio || 1;
            const viewport = page.getViewport({ scale: (container.clientWidth / natural.width) * density });
            canvas.width = viewport.width;
            canvas.height = viewport.height;
            container.append(canvas);
            await page.render({ canvasContext: canvas.getContext('2d'), viewport }).promise;
        }
    } finally {
        signal?.removeEventListener('abort', cancel);
        await loading.destroy();
    }
}
